// Helix's written read as its own channel of the synergy-loop.v1 narrative. No imports: this is
// client-reachable, and the decoder below is the only thing between a stream and the page.

/** Helix's written read is this many paragraphs at most; "What to do" is the page's own list. */
export const SYNERGY_LOOP_SUMMARY_MAX_PARAGRAPHS = 2;

/**
 * Helix's written read as its own channel: ordered Markdown paragraphs that the shared governed
 * compiler published, and nothing else. It never carries a list, a heading or an action, because
 * the actions beside it are engine-derived rows; a reader renders this channel and never has to
 * tell a summary paragraph from an action by its words. When present it holds at least one
 * paragraph and at most `SYNERGY_LOOP_SUMMARY_MAX_PARAGRAPHS`, and each entry is exactly one
 * paragraph; no written read means it is absent.
 */
export type SynergyLoopSummary = Readonly<{ paragraphs: readonly string[] }>;

/** The narrative's summary field, declared beside its reader so the shape and its rules sit together. */
export type SynergyLoopSummaryField = Readonly<{
  /** The written read (a model narrative only); joined by a blank line it is `answerMarkdown`. */
  summary?: SynergyLoopSummary;
}>;

/**
 * A line that opens a block other than a paragraph: a heading, a bullet, a numbered item, a quote,
 * a table row, a fence, an HTML block or a rule. (A line that merely continues a paragraph, such
 * as "3.5M is behind plan" or "**Off track.**", opens none.)
 */
const OPENS_A_BLOCK = new RegExp('^ {0,3}(?:#{1,6}(?:[ \\t]|$)|[-*+](?:[ \\t]|$)'
  + '|\\d{1,9}[.)](?:[ \\t]|$)|>|\\||`{3}|~{3}|<[A-Za-z/!?]|([-*_])(?:[ \\t]*\\1){2,}[ \\t]*$'
  + '|=+[ \\t]*$)', 'u');
const INDENTED_CODE = /^(?: {4}|\t)/u;

/** One blank-line-separated block is a paragraph when no line of it opens another construct. */
function isParagraph(block: string): boolean {
  const lines = block.split('\n');
  return !INDENTED_CODE.test(lines[0]) && !lines.some(line => OPENS_A_BLOCK.test(line));
}

/**
 * Reads a summary by its paragraphs, not by its array entries: an entry may hold several, split
 * at blank lines. It reads whole or not at all. One blank or non-text entry, or one block that is
 * not a paragraph (a list, a heading, a quote, a table, a fence, indented code, a rule), drops the
 * whole summary, never a part of it. A summary of more than the allowed paragraphs is cut to the
 * first of them. What comes back is one paragraph per entry.
 */
export function readSynergyLoopSummary(value: unknown): SynergyLoopSummary | null {
  const entries = typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as { paragraphs?: unknown }).paragraphs : undefined;
  if (!Array.isArray(entries)) return null;
  const paragraphs: string[] = [];
  for (const entry of entries) {
    if (typeof entry !== 'string' || entry.trim() === '') return null;
    for (const block of entry.replace(/\r\n?/gu, '\n').split(/\n[ \t]*\n/u)) {
      const own = block.replace(/^\n+/u, '');
      if (own.trim() === '') continue;
      if (!isParagraph(own)) return null;
      paragraphs.push(own.trim());
    }
  }
  return paragraphs.length === 0 ? null
    : { paragraphs: paragraphs.slice(0, SYNERGY_LOOP_SUMMARY_MAX_PARAGRAPHS) };
}

/** What a stream's `summary` decodes to as part of a narrative: nothing unless it reads whole. */
export function readSummaryField(value: unknown): SynergyLoopSummaryField {
  const summary = readSynergyLoopSummary(value);
  return summary ? { summary } : {};
}
